import { renderSurfaceKind, isCompiledSurfaceKind }
  from "%/dynamic-component-editor/helpers/context-error-report";
import App from "@bloglovin/inspire-engine/App";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import StoryCardClickBehavior from "%/dynamic-component-editor/options/story-card-click-behavior";
import {
  SmartFeedEntityDynamicElementMixin,
} from "%/dynamic-component-editor/mixins/smart-feed-entity-dynamic-element-mixin";
import { readBoundContextName, resolveBoundEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";

const STORY_ENTITY_TYPE = "Story";

const EMPTY_STORY_ENTITY = Object.freeze({
  id: 0,
  authorId: 0,
  authorName: "",
  authorAvatar: "",
  title: "",
  thumbnail: "",
  text: "",
  body: [],
  hashtags: [],
  timestampCreated: 0,
  publishedAt: 0,
  estimatedReadingSeconds: 0,
  likeCount: 0,
  voteScore: 0,
  upvoteCount: 0,
  downvoteCount: 0,
  commentCount: 0,
  likeStatus: false,
  numShares: 0,
  nestedUrl: "",
  storyType: "",
  metadata: [],
});

export const BaseStoryCardElementMixin = {
  extends: SmartFeedEntityDynamicElementMixin,
  inject: {
    pageEntities: { default: null },
    useExampleData: { default: () => () => false },
    scopedProvidedContexts: { default: null },
  },
  data() {
    return {
      resolvedUrlStoryId: 0,
    };
  },
  created() {
    if (this.editMode || this.isPreviewMode) {
      return;
    }
    if (!this.entityId && (!this.boundContextName || this.boundContextName === "story") && typeof window !== "undefined") {
      const injectedStoryId = window.blclient?.data?.readerStoryId;
      if (injectedStoryId) {
        this.resolvedUrlStoryId = Number(injectedStoryId);
      } else if (isCompiledSurfaceKind(renderSurfaceKind())) {
        const match = window.location.pathname.match(/\/[^/]+\/(\d+)\/?$/);
        if (match) {
          this.resolvedUrlStoryId = Number(match[1]);
        }
      }
    }
  },
  computed: {
    isPreviewMode() {
      const fn = this.useExampleData;
      return typeof fn === "function" ? Boolean(fn()) : Boolean(fn);
    },
    boundContextName() {
      return readBoundContextName(this);
    },
    storyEntityId() {
      return resolveBoundEntityId(this, "story", () => this.resolvedUrlStoryId);
    },
    hasResolvedStoryEntity() {
      const id = this.storyEntityId;
      if (!id) {
        return false;
      }
      const entity = App.get(contextualAppName()).getEntity(STORY_ENTITY_TYPE, { id });
      return Boolean(entity && entity.isHydrated);
    },
    hideUntilStoryEntityResolved() {
      return !this.editMode && !this.isPreviewMode && !this.hasResolvedStoryEntity;
    },
    countPending() {
      return this.hideUntilStoryEntityResolved;
    },
    storyHref() {
      return StoryCardClickBehavior.storyHref(
        this.links.web,
        {
          authorProfile: this.entity.authorId,
          postPage: this.entity.id,
          nestedPath: this.entity.nestedUrl,
        },
        this.getOptionValue(StoryCardClickBehavior),
      );
    },
    isNotALink() {
      return this.getOptionValue(StoryCardClickBehavior) === StoryCardClickBehavior.optionValues.notALink;
    },
    shouldShowLink() {
      return !this.isNotALink;
    },
  },
  methods: {
    getRealEntity() {
      if (this.isPreviewMode) {
        return this.$options.exampleEntity ?? EMPTY_STORY_ENTITY;
      }
      const id = this.storyEntityId;
      if (!id) {
        return EMPTY_STORY_ENTITY;
      }
      const entity = App.get(contextualAppName()).getEntity(STORY_ENTITY_TYPE, { id });
      return entity || EMPTY_STORY_ENTITY;
    },
  },
  exampleEntity: {
    "id": 1,
    "authorAvatar": "https://inspirationprod.blob.core.windows.net/inspiration-cdn/images/john-smith-profile-picture.webp",
    "authorId": 1,
    "authorName": "John Smith",
    "title": "The Power of Personal Style: Why Fashion Is More Than Just Clothes",
    "publicationName": "None",
    "likeCount": 0,
    "voteScore": 0,
    "upvoteCount": 0,
    "downvoteCount": 0,
    "hashtags": ["test", "post"],
    "likeStatus": true,
    "isExclusive": false,
    "thumbnail": "https://inspirationprod.blob.core.windows.net/inspiration-cdn/images/fashion-story-sample-image.webp",
    "text": " Write something amazing!",
    "status": "active",
    "timestampCreated": 1733412192,
    "publishedAt": 1733412192,
    "commentCount": 12,
    "estimatedReadingSeconds": 480,
    "numShares": 0,
    "nestedUrl": "",
    "storyType": "",
    "metadata": [
      { key: "icon", value: "mdi:star-outline" },
      { key: "genre", value: "Drama" },
      { key: "release_date", value: "1709251200" },
      { key: "synopsis", value: "A gripping tale of triumph." },
      { key: "servings", value: "4" },
      { key: "servings_descriptor", value: "servings" },
      { key: "prep_minutes", value: "15" },
      { key: "prep_minutes_descriptor", value: "minutes" },
      { key: "ingredients", value: "Flour, sugar, eggs, butter" },
      { key: "sets", value: "3" },
      { key: "sets_descriptor", value: "sets" },
      { key: "reps", value: "12" },
      { key: "reps_descriptor", value: "per side" },
      { key: "season_number", value: "1" },
      { key: "episode_count", value: "10" },
      { key: "episode_count_descriptor", value: "episodes" },
      { key: "episode_number", value: "1" },
      { key: "duration_minutes", value: "42" },
      { key: "duration_minutes_descriptor", value: "minutes" },
      { key: "air_date", value: "1709251200" },
      { key: "author", value: "Jane Doe" },
      { key: "read_minutes", value: "5" },
      { key: "read_minutes_descriptor", value: "min read" },
      { key: "summary", value: "A concise overview of the key points." },
      { key: "transcript", value: "Welcome back — today we're covering the fundamentals." },
      { key: "subtitle", value: "Beginner Full-Body Program" },
      { key: "description", value: "A 4-week plan to build foundational strength and mobility." },
      { key: "duration_weeks", value: "4" },
      { key: "duration_weeks_descriptor", value: "weeks" },
      { key: "days_per_week", value: "3" },
      { key: "difficulty_level", value: "intermediate" },
      { key: "average_session_minutes", value: "45" },
      { key: "average_session_minutes_descriptor", value: "minutes" },
      { key: "week_number", value: "1" },
      { key: "day_number", value: "1" },
      { key: "day_type", value: "legs" },
      { key: "type_description", value: "Focus on legs; remember to stretch afterward." },
      { key: "weight", value: "100" },
      { key: "weight_descriptor", value: "kg" },
      { key: "duration", value: "30" },
      { key: "duration_descriptor", value: "seconds" },
      { key: "rest", value: "60" },
      { key: "rest_descriptor", value: "seconds" },
      { key: "tempo", value: "3-0-1" },
    ],
  },
  elementPanelSettings: {
    elementGroup: "storyElements",
  },
};
